import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../lib/api";
import { RevealLine, FadeUp, RevealMaskScroll } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import { useSiteContent } from "../context/SiteContentContext";
import EditableImage from "../components/admin/EditableImage";
import EditableText from "../components/admin/EditableText";

const Q = "?auto=compress&cs=tinysrgb&w=1400";
const IMG = {
  bride1: "https://images.pexels.com/photos/38526712/pexels-photo-38526712.jpeg" + Q,
  bride2: "https://images.pexels.com/photos/28405815/pexels-photo-28405815.jpeg" + Q,
  saree1: "https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg" + Q,
  saree_red: "https://images.pexels.com/photos/36951188/pexels-photo-36951188.jpeg" + Q,
  jewel1: "https://images.pexels.com/photos/25399495/pexels-photo-25399495.jpeg" + Q,
  jewel2: "https://images.pexels.com/photos/35395104/pexels-photo-35395104.jpeg" + Q,
  craft1: "https://images.pexels.com/photos/13206031/pexels-photo-13206031.jpeg" + Q,
  craft2: "https://images.pexels.com/photos/6461101/pexels-photo-6461101.jpeg" + Q,
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.6]);
  const site = useSiteContent();
  const heroImg = site.media("home_hero", IMG.bride1);
  const kicker = site.text("home_hero_kicker", "The Wedding Edit · Autumn 2025");

  const saveHero = async (url) => {
    await site.saveMedia("home_hero", url);
    toast.success("Hero image updated");
  };
  const saveKicker = async (val) => {
    await site.saveText("home_hero_kicker", val);
    toast.success("Kicker updated");
  };

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <EditableImage
          src={heroImg}
          onSave={saveHero}
          label="Hero Image"
          alt="Amraiya bridal couture"
          className="h-full w-full"
          imgClassName="h-full w-full object-cover object-center"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30 pointer-events-none" />

      <div className="relative z-10 flex h-full flex-col justify-end px-5 md:px-10 pb-20 md:pb-24 max-w-[1600px] mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4, duration: 1 }}
          className="overline text-gold mb-6"
        >
          <EditableText as="span" value={kicker} onSave={saveKicker} label="Hero Kicker">
            {kicker}
          </EditableText>
        </motion.p>
        <h1 className="font-serif text-ivory text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          <RevealLine delay={2.4}>{site.text("home_hero_title_1", "Heritage,")}</RevealLine>
          <RevealLine delay={2.55} className="italic text-gold pl-8 md:pl-16">{site.text("home_hero_title_2", "reimagined")}</RevealLine>
          <RevealLine delay={2.7}>{site.text("home_hero_title_3", "for the modern muse.")}</RevealLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/shop" className="btn-ink group flex items-center gap-3 px-9 py-4 overline" data-testid="hero-shop-btn">
            {site.text("home_hero_cta_1", "Shop Now")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/collections" className="group flex items-center gap-3 px-9 py-4 overline text-ivory border border-ivory/40 hover:bg-ivory hover:text-ink transition-colors" data-testid="hero-collection-btn">
            {site.text("home_hero_cta_2", "Explore Collection")}
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.4, duration: 1 }}
        className="absolute bottom-8 right-10 z-10 hidden md:flex items-center gap-3 text-ivory/70"
      >
        <span className="overline">Scroll</span>
        <span className="h-12 w-px bg-ivory/40" />
      </motion.div>
    </section>
  );
}

function CollectionRibbon() {
  const site = useSiteContent();
  const raw = site.text("home_marquee_text", "");
  const items = raw
    ? raw.split(",").map((s) => s.trim()).filter(Boolean)
    : ["The Wedding Edit", "New Arrivals", "Handcrafted in India", "Made to Order", "Timeless Couture"];
  return (
    <div className="border-y border-line bg-ivory py-5" data-testid="marquee">
      <Marquee speed={30} gradient={false} autoFill>
        {items.map((t, i) => (
          <span key={i} className="mx-10 font-serif text-3xl md:text-4xl italic text-ink/80">
            {t} <span className="text-gold not-italic mx-6">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function FeaturedCollections({ categories, onCategoryUpdate }) {
  const site = useSiteContent();
  const bento = categories.slice(0, 5);
  if (!bento.length) return null;
  return (
    <section className="px-5 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="featured-collections">
      <div className="mb-14 flex items-end justify-between">
        <div>
          <FadeUp><p className="overline text-gold-deep mb-4">{site.text("home_collections_kicker", "01 — Collections")}</p></FadeUp>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-none">
            <RevealMaskScroll>{site.text("home_collections_title_1", "The Curated")}</RevealMaskScroll>
            <RevealMaskScroll delay={0.1} className="italic pl-10">{site.text("home_collections_title_2", "Wardrobe")}</RevealMaskScroll>
          </h2>
        </div>
        <Link to="/collections" className="hidden md:flex items-center gap-2 overline link-underline">View all <ArrowUpRight size={15} /></Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[220px] md:auto-rows-[300px]">
        <CollectionCard cat={bento[0]} className="col-span-2 row-span-2" big onUpdate={onCategoryUpdate} />
        {bento.slice(1, 5).map((c) => (
          <CollectionCard key={c.slug} cat={c} className="col-span-1 row-span-1" onUpdate={onCategoryUpdate} />
        ))}
      </div>
    </section>
  );
}

function CollectionCard({ cat, className, big, onUpdate }) {
  const saveImg = async (url) => {
    const payload = { name: cat.name, slug: cat.slug, image: url, description: cat.description || "" };
    const { data } = await api.put(`/admin/categories/${cat.id}`, payload);
    onUpdate && onUpdate(data);
    toast.success("Category image updated");
  };
  return (
    <FadeUp className={className}>
      <div className="group relative block h-full w-full overflow-hidden bg-[#efece6]" data-testid={`collection-card-${cat.slug}`}>
        <Link to={`/shop?category=${cat.slug}`} className="block h-full w-full">
          <EditableImage
            src={cat.image || undefined}
            onSave={saveImg}
            label={`Category · ${cat.name}`}
            alt={cat.name}
            loading="lazy"
            className="h-full w-full"
            imgClassName="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 p-5 md:p-7">
            <h3 className={`font-serif text-ivory tracking-tight ${big ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"}`}>{cat.name}</h3>
            <span className="mt-2 flex items-center gap-2 overline text-ivory/80 opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0">
              Discover <ArrowRight size={13} />
            </span>
          </div>
        </Link>
      </div>
    </FadeUp>
  );
}

function Occasions({ occasions, onOccasionUpdate }) {
  const site = useSiteContent();
  return (
    <section className="bg-ink text-ivory py-24 md:py-32" data-testid="occasions">
      <div className="px-5 md:px-10 max-w-[1600px] mx-auto">
        <div className="mb-14 text-center">
          <FadeUp><p className="overline text-gold mb-4">{site.text("home_occasions_kicker", "02 — Shop by Occasion")}</p></FadeUp>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tight">
            <RevealMaskScroll>{site.text("home_occasions_title", "Dress the moment")}</RevealMaskScroll>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {occasions.map((o, i) => (
            <FadeUp key={o.slug} delay={i * 0.05}>
              <div className="group relative block aspect-[3/4] overflow-hidden" data-testid={`occasion-${o.slug}`}>
                <Link to={`/shop?occasion=${o.name}`} className="block h-full w-full">
                  <EditableImage
                    src={o.image || undefined}
                    onSave={async (url) => {
                      const { data } = await api.put(`/admin/occasions/${o.id}`, {
                        name: o.name, image: url, tagline: o.tagline || "",
                      });
                      onOccasionUpdate && onOccasionUpdate(data);
                      toast.success("Occasion image updated");
                    }}
                    label={`Occasion · ${o.name}`}
                    alt={o.name}
                    loading="lazy"
                    className="h-full w-full"
                    imgClassName="h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors pointer-events-none" />
                  <span className="absolute inset-x-0 bottom-5 text-center font-serif text-2xl">{o.name}</span>
                </Link>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewArrivals({ products, onQuickView, onProductUpdate }) {
  const site = useSiteContent();
  return (
    <section className="px-5 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="new-arrivals">
      <div className="mb-14 flex items-end justify-between">
        <div>
          <FadeUp><p className="overline text-gold-deep mb-4">{site.text("home_new_kicker", "03 — Just In")}</p></FadeUp>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-none">
            <RevealMaskScroll>{site.text("home_new_title", "New Arrivals")}</RevealMaskScroll>
          </h2>
        </div>
        <Link to="/shop?is_new=true" className="hidden md:flex items-center gap-2 overline link-underline">View all <ArrowUpRight size={15} /></Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6">
        {products.map((p, i) => (
          <ProductCard key={p.slug} product={p} index={i} onQuickView={onQuickView} onProductUpdate={onProductUpdate} />
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  const site = useSiteContent();
  const count = Math.max(1, Math.min(3, parseInt(site.text("home_manifesto_count", "3"), 10) || 3));
  const chapters = [
    { n: "01", title: site.text("home_manifesto_ch1_title", "The Karigar's hand"), img: site.media("home_manifesto_ch1", IMG.craft1), text: site.text("home_manifesto_ch1_body", "Every thread is placed by artisans whose craft is inherited across generations. Weeks of zardozi, aari and pitta work live inside a single garment.") },
    { n: "02", title: site.text("home_manifesto_ch2_title", "Cloth with a memory"), img: site.media("home_manifesto_ch2", IMG.craft2), text: site.text("home_manifesto_ch2_body", "We source raw silk, organza and Banarasi weaves from the looms that first perfected them — fabrics that hold light, movement and history.") },
    { n: "03", title: site.text("home_manifesto_ch3_title", "Made only for you"), img: site.media("home_manifesto_ch3", IMG.jewel2), text: site.text("home_manifesto_ch3_body", "Nothing is mass produced. Each piece is cut and tailored to order, so that what you wear exists, quite literally, for no one else.") },
  ].slice(0, count);
  return (
    <section className="bg-[#f2efe9] py-24 md:py-36" data-testid="manifesto">
      <div className="px-5 md:px-10 max-w-[1400px] mx-auto">
        <FadeUp><p className="overline text-gold-deep mb-4 text-center">04 — The Manifesto</p></FadeUp>
        <h2 className="font-serif text-5xl md:text-8xl tracking-tight text-center mb-24 leading-none">
          <RevealMaskScroll>A house built</RevealMaskScroll>
          <RevealMaskScroll delay={0.1} className="italic">on patience</RevealMaskScroll>
        </h2>
        <div className="space-y-28">
          {chapters.map((c, i) => (
            <div key={c.n} className={`grid md:grid-cols-2 gap-10 md:gap-20 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
              <FadeUp className="md:[direction:ltr]">
                <ParallaxImage src={c.img} alt={c.title} />
              </FadeUp>
              <div className="md:[direction:ltr]">
                <span className="font-serif text-8xl md:text-9xl text-gold/40 leading-none">{c.n}</span>
                <h3 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 mb-6">{c.title}</h3>
                <p className="max-w-md text-ink-soft leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParallaxImage({ src, alt }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="aspect-[4/5] overflow-hidden bg-[#efece6]">
      <motion.img style={{ y, scale: 1.15 }} src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
}

function BestSellers({ products, onQuickView, onProductUpdate }) {
  const site = useSiteContent();
  return (
    <section className="py-24 md:py-32" data-testid="best-sellers">
      <div className="px-5 md:px-10 max-w-[1600px] mx-auto mb-12 flex items-end justify-between">
        <div>
          <FadeUp><p className="overline text-gold-deep mb-4">{site.text("home_bestsellers_kicker", "05 — Most Coveted")}</p></FadeUp>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tight"><RevealMaskScroll>{site.text("home_bestsellers_title", "Best Sellers")}</RevealMaskScroll></h2>
        </div>
        <Link to="/shop?is_bestseller=true" className="hidden md:flex items-center gap-2 overline link-underline">View all <ArrowUpRight size={15} /></Link>
      </div>
      <div className="flex gap-5 overflow-x-auto px-5 md:px-10 pb-4 snap-x" data-testid="best-sellers-scroll">
        {products.map((p, i) => (
          <div key={p.slug} className="min-w-[260px] md:min-w-[340px] snap-start">
            <ProductCard product={p} index={i} onQuickView={onQuickView} onProductUpdate={onProductUpdate} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  const site = useSiteContent();
  return (
    <section className="bg-ink text-ivory py-24 md:py-32" data-testid="testimonials">
      <div className="px-5 md:px-10 max-w-[1400px] mx-auto">
        <FadeUp><p className="overline text-gold text-center mb-16">{site.text("home_testimonials_header", "Words from our muses")}</p></FadeUp>
        <div className="grid md:grid-cols-3 gap-10">
          {items.map((t, i) => (
            <FadeUp key={i} delay={i * 0.1} className="flex flex-col items-center text-center">
              <img src={t.image || undefined} alt={t.name} className="h-20 w-20 rounded-full object-cover mb-6" />
              <p className="font-serif text-2xl italic leading-snug mb-6">“{t.quote}”</p>
              <p className="overline text-gold">{t.name}</p>
              <p className="text-xs text-white/40 mt-1">{t.location}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function InstagramGallery() {
  const site = useSiteContent();
  const imgs = [IMG.bride2, IMG.saree1, IMG.jewel1, IMG.saree_red, IMG.jewel2, IMG.bride1];
  return (
    <section className="py-24 md:py-32 px-5 md:px-10 max-w-[1600px] mx-auto" data-testid="instagram">
      <div className="text-center mb-14">
        <FadeUp><p className="overline text-gold-deep mb-3">{site.text("home_instagram_handle", "@amraiya")}</p></FadeUp>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight"><RevealMaskScroll>{site.text("home_instagram_title", "Follow the atelier")}</RevealMaskScroll></h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {imgs.map((src, i) => (
          <FadeUp key={i} delay={(i % 6) * 0.05}>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="group relative block aspect-square overflow-hidden">
              <img src={src} alt="Instagram" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 grid place-items-center bg-ink/0 group-hover:bg-ink/40 transition-colors">
                <ArrowUpRight className="text-ivory opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const [qvOpen, setQvOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/occasions"),
      api.get("/products", { params: { is_new: true, limit: 4 } }),
      api.get("/products", { params: { is_bestseller: true, limit: 8 } }),
      api.get("/testimonials"),
    ]).then(([c, o, n, b, t]) => {
      setCategories(c.data);
      setOccasions(o.data);
      setNewArrivals(n.data);
      setBestSellers(b.data);
      setTestimonials(t.data);
    });
  }, []);

  const openQuickView = (p) => { setQuickView(p); setQvOpen(true); };
  const site = useSiteContent();
  const show = (key) => site.text(key, "on") !== "off";

  return (
    <div data-testid="home-page">
      <Hero />
      <CollectionRibbon />
      {show("section_collections") && (
        <FeaturedCollections
          categories={categories}
          onCategoryUpdate={(updated) => setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
        />
      )}
      {show("section_occasions") && (
        <Occasions
          occasions={occasions}
          onOccasionUpdate={(updated) => setOccasions((prev) => prev.map((o) => (o.id === updated.id ? updated : o)))}
        />
      )}
      {show("section_new_arrivals") && (
        <NewArrivals
          products={newArrivals}
          onQuickView={openQuickView}
          onProductUpdate={(updated) => setNewArrivals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))}
        />
      )}
      {show("section_manifesto") && <Manifesto />}
      {show("section_bestsellers") && (
        <BestSellers
          products={bestSellers}
          onQuickView={openQuickView}
          onProductUpdate={(updated) => setBestSellers((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))}
        />
      )}
      {show("section_testimonials") && <Testimonials items={testimonials} />}
      {show("section_instagram") && <InstagramGallery />}
      <QuickView product={quickView} open={qvOpen} onOpenChange={setQvOpen} />
    </div>
  );
}
